import { booleanAttribute, Component, EventEmitter, Input, Output } from '@angular/core';
import { Job } from '../../../job.model';
import { ProjectService } from '../../../../../project.service';
import { ServiceGroup } from '../../../../../../service-groups/service-group.model';
import { AuthService } from '../../../../../../auth/auth.service';
import { LoggerService } from '../../../../../../core/logger/logger.service';
import { NotificationService } from '../../../../../../core/notification/notification.service';
import { UtilsService } from '../../../../../../core/utils/utils.service';
import { SettingsService } from '../../../../../../settings/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { AllowedRelation } from '../../../../../../service-groups/articles/article-details/field/field.model';

@Component({
    selector: 'app-new-job-linking',
    templateUrl: './new-job-linking.component.html',
    styleUrls: ['./new-job-linking.component.scss'],
})
export class NewJobLinkingComponent {
    @Input() currentJobId: string;
    @Input({ transform: booleanAttribute }) autoLinkNewCreatedJobs = false;
    @Input() linkFieldName: string = null; // name of the field that is linked
    @Input() allowedRelations: Array<AllowedRelation> = [];

    @Output() jobLinkConfirmed = new EventEmitter<{ linkType: 'existing' | 'new'; job: Job }>();

    // helper
    scanEnabled = false;
    lastJobLinkSearchString = '';
    jobLinkSearchResultsLoading = false;
    jobLinkSearchResults: Array<Job> = [];

    newJobWithLinkingModalVisible = false;
    linkExistingJobModalVisible = false;

    workableServiceGroupsLoading = false;
    workableServiceGroups: Array<ServiceGroup> = [];

    constructor(
        private projectSrv: ProjectService,
        private authSrv: AuthService,
        private logger: LoggerService,
        private notify: NotificationService,
        public utils: UtilsService,
        public settingsSrv: SettingsService,
        private translate: TranslateService
    ) {}

    /**
     * Triggered when the user wants to choose a new linkedJob. Shows the dialog to choose between creating a new job or linking an existing job
     */
    public loadWorkableServiceGroups() {
        this.workableServiceGroupsLoading = true;
        this.workableServiceGroups = [];

        // get the workable service groups of viewer
        if (this.settingsSrv.currentUserRights.canManageProjects) {
            // all service groups available
            this.workableServiceGroups = this.utils.clone(this.projectSrv.currentProjectServiceGroupList$.getValue());
        } else if (this.authSrv.isMobileLicense) {
            // check if there are workable service groups defined, otherwise all are ok
            if (this.projectSrv.currentUserProjectSharingData.projectPermissions.workableServiceGroups.length > 0) {
                // filter out all service groups which are not allowed
                this.workableServiceGroups = this.utils.clone(this.projectSrv.currentProjectServiceGroupList$.getValue()).filter((x) => {
                    return this.projectSrv.currentUserProjectSharingData.projectPermissions.workableServiceGroups.includes(x._id);
                });
            } else {
                // as there are no workable service groups defined, all are ok
                this.workableServiceGroups = this.utils.clone(this.projectSrv.currentProjectServiceGroupList$.getValue());
            }
        } else if (this.authSrv.isViewerLicense) {
            if (Array.isArray(this.projectSrv.currentUserProjectSharingData.projectPermissions.viewerWorkableServiceGroups)) {
                this.workableServiceGroups = this.utils.clone(this.projectSrv.currentProjectServiceGroupList$.getValue()).filter((x) => {
                    return this.projectSrv.currentUserProjectSharingData.projectPermissions.viewerWorkableServiceGroups.includes(x._id);
                });
            }
        }

        // check if there are allowedRelations (service-groups selected) -> so we have to filter the workableServiceGroups
        if (this.allowedRelations?.length > 0) {
            const allowedIds = new Set(this.allowedRelations.map((r) => r._id));
            const allowedNames = new Set(this.allowedRelations.map((r) => r.name));
            this.workableServiceGroups = this.workableServiceGroups.filter(
                (sg) => allowedIds.has(sg._id) || allowedNames.has(sg.name) || (sg.originalId != null && allowedIds.has(sg.originalId))
            );
        }

        this.workableServiceGroupsLoading = false;
    }

    /**
     * Triggered when the user wants to link an existing job. Shows the dialog to choose a job
     */
    onLinkExistingJob() {
        this.loadWorkableServiceGroups();
        this.lastJobLinkSearchString = '';
        this.jobLinkSearchResults = [];
        this.scanEnabled = true;
        this.linkExistingJobModalVisible = true;
    }

    /**
     * Triggered when the user entered something into the search field or scanned a code. Searches for jobs and displays results
     * @param scanResult
     */
    onExistingJobLinkScanResult(scanResult: string) {
        this.logger.log('[NEW JOB LINKING] got scan result ', scanResult);
        this.jobLinkSearchResultsLoading = true;
        this.lastJobLinkSearchString = scanResult;
        this.scanEnabled = false;

        this.projectSrv.searchJobsInProject(scanResult, this.projectSrv.currentProject$.getValue()._id, 'asc').subscribe(
            (projectBasicJobs) => {
                let searchResults: Array<Job> = [];
                const visibleGroupIds = this.projectSrv.currentUserProjectSharingData.projectPermissions.visibleServiceGroups;

                for (let jobInfo of projectBasicJobs) {
                    if (
                        (this.settingsSrv.currentUserRights.canManageProjects ||
                            visibleGroupIds.length == 0 ||
                            visibleGroupIds.indexOf(jobInfo.serviceGroupId) !== -1) &&
                        this.currentJobId != jobInfo._id
                    ) {
                        searchResults.push(jobInfo);
                    }
                }
                this.jobLinkSearchResults = searchResults;
                this.jobLinkSearchResultsLoading = false;
                if (searchResults.length === 1) {
                    this.selectExistingJobForLinking(searchResults[0]); // we can instantly select the job if only 1 search result exists
                }
                this.logger.log('[NEW JOB LINKING] built search result for scan', this.jobLinkSearchResults);
            },
            (searchErr) => {
                this.logger.error('[JOB LINKING] could not complete job search for scanResult ' + scanResult, searchErr);
                this.notify.error(this.translate.instant('projects.jobs.details.linking.error.search'));
                this.utils.sentryCaptureException(searchErr);
                this.onCancelExistingJobLinkSearch();
            }
        );
    }

    /*+
    Triggered when the user selects an existing job from the search results for linking
     */
    selectExistingJobForLinking(job) {
        this.onConfirmJobLink('existing', job);
        this.linkExistingJobModalVisible = false;
    }

    /**
     * Triggered when the job linking gets restarted (new search)
     */
    onRestartJobLinkSearch() {
        this.jobLinkSearchResults = [];
        this.lastJobLinkSearchString = '';
        this.jobLinkSearchResultsLoading = false;
        this.scanEnabled = true;
    }

    /**
     * Triggered when the job linking search gets cancelled
     */
    onCancelExistingJobLinkSearch() {
        this.scanEnabled = false;
        this.linkExistingJobModalVisible = false;
        this.jobLinkSearchResults = [];
        this.jobLinkSearchResultsLoading = false;
        this.lastJobLinkSearchString = '';
    }

    /**
     * Triggered when the user wants to create a new job, which is linked to the current job
     */
    onLinkNewJob() {
        this.loadWorkableServiceGroups();
        if (this.workableServiceGroups.length === 1) {
            this.createNewJobWithLinking(this.workableServiceGroups[0]);
        } else {
            this.newJobWithLinkingModalVisible = true;
        }
    }

    /**
     * Triggered when the viewer-user wants to create a job which is linked to the current job
     */
    createNewJobWithLinking(serviceGroup: ServiceGroup) {
        this.workableServiceGroupsLoading = true;
        this.projectSrv
            .createNewJob(serviceGroup, this.autoLinkNewCreatedJobs ? [this.currentJobId] : null, null, {
                jobName: this.linkFieldName,
                referringJobId: this.currentJobId,
            })
            .subscribe(
                async (newJob) => {
                    this.logger.log('[NEW JOB LINKING] created new job with linking', newJob);
                    this.newJobWithLinkingModalVisible = false;
                    this.workableServiceGroupsLoading = false;

                    this.onConfirmJobLink('new', newJob);
                },
                (err) => {
                    this.logger.error('[JOB LINKINGS] could not create job with linking', err);
                    this.newJobWithLinkingModalVisible = false;
                    this.workableServiceGroupsLoading = false;
                    this.notify.error(this.translate.instant('projects.jobs.details.linking.error.create'));
                    this.utils.sentryCaptureException(err);
                }
            );
    }

    /**
     * Triggered when the new job linking gets cancelled
     */
    onCancelNewJobLink() {
        this.newJobWithLinkingModalVisible = false;
    }

    /**
     * Triggered when an job is selected for linking
     * @param linkType
     * @param job
     */
    onConfirmJobLink(linkType: 'existing' | 'new', job: Job) {
        this.jobLinkConfirmed.emit({ linkType: linkType, job: job });
    }
}
